import { useState, useEffect } from 'react';
/* eslint-disable react-hooks/exhaustive-deps -- plan loader is intentionally keyed by branch */
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/layout/AdminLayout';
import { useSucursal } from '../../utils/sucursalContext';

type RecurringProvider = 'mercadopago' | 'transbank_oneclick_mall' | 'fintoc' | 'flow';

const RECURRING_PROVIDER_OPTIONS: { value: RecurringProvider; label: string }[] = [
  { value: 'mercadopago', label: 'Mercado Pago' },
  { value: 'flow', label: 'Flow — tarjeta recurrente' },
  { value: 'fintoc', label: 'Fintoc — PAC recurrente' },
  { value: 'transbank_oneclick_mall', label: 'Transbank Oneclick (cuando se habilite)' },
];

const recurringProviderLabel = (provider: RecurringProvider) =>
  RECURRING_PROVIDER_OPTIONS.find((option) => option.value === provider)?.label || provider;

interface Plan {
  id: string;
  gimnasio_id?: string | null;
  nombre: string;
  descripcion: string;
  precio: number;
  duracion_dias: number;
  modalidad_cobro?: 'unico' | 'recurrente';
  proveedor_recurrente?: RecurringProvider | null;
  proveedores_recurrentes?: RecurringProvider[] | null;
  visible_kiosco?: boolean;
  permitir_webpay_plus_kiosco?: boolean;
  max_dias_semana?: number | null;
  max_sesiones_periodo?: number | null;
  permitir_multiples_reservas_dia?: boolean | null;
  acceso_horario?: 'todo_dia' | 'manana' | 'tarde' | string | null;
  acceso_hora_inicio?: string | null;
  acceso_hora_fin?: string | null;
  beneficios: string[];
  activo: boolean;
  visible_app_mobile?: boolean;
  color: string;
  created_at: string;
  updated_at: string;
}

const COLOR_OPTIONS = [
  { key: 'teal', label: 'Teal', swatch: 'bg-teal-500', bg: 'bg-teal-50', border: 'border-teal-200', icon: 'bg-teal-100 text-teal-600', badge: 'bg-teal-100 text-teal-700', accent: 'text-teal-600', ring: 'ring-teal-500' },
  { key: 'emerald', label: 'Esmeralda', swatch: 'bg-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'bg-emerald-100 text-emerald-600', badge: 'bg-emerald-100 text-emerald-700', accent: 'text-emerald-600', ring: 'ring-emerald-500' },
  { key: 'amber', label: 'Ámbar', swatch: 'bg-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', icon: 'bg-amber-100 text-amber-600', badge: 'bg-amber-100 text-amber-700', accent: 'text-amber-600', ring: 'ring-amber-500' },
  { key: 'rose', label: 'Rosa', swatch: 'bg-rose-500', bg: 'bg-rose-50', border: 'border-rose-200', icon: 'bg-rose-100 text-rose-600', badge: 'bg-rose-100 text-rose-700', accent: 'text-rose-600', ring: 'ring-rose-500' },
  { key: 'orange', label: 'Naranja', swatch: 'bg-orange-500', bg: 'bg-orange-50', border: 'border-orange-200', icon: 'bg-orange-100 text-orange-600', badge: 'bg-orange-100 text-orange-700', accent: 'text-orange-600', ring: 'ring-orange-500' },
  { key: 'cyan', label: 'Cian', swatch: 'bg-cyan-500', bg: 'bg-cyan-50', border: 'border-cyan-200', icon: 'bg-cyan-100 text-cyan-600', badge: 'bg-cyan-100 text-cyan-700', accent: 'text-cyan-600', ring: 'ring-cyan-500' },
  { key: 'lime', label: 'Lima', swatch: 'bg-lime-500', bg: 'bg-lime-50', border: 'border-lime-200', icon: 'bg-lime-100 text-lime-600', badge: 'bg-lime-100 text-lime-700', accent: 'text-lime-600', ring: 'ring-lime-500' },
  { key: 'fuchsia', label: 'Fucsia', swatch: 'bg-fuchsia-500', bg: 'bg-fuchsia-50', border: 'border-fuchsia-200', icon: 'bg-fuchsia-100 text-fuchsia-600', badge: 'bg-fuchsia-100 text-fuchsia-700', accent: 'text-fuchsia-600', ring: 'ring-fuchsia-500' },
  { key: 'sky', label: 'Cielo', swatch: 'bg-sky-500', bg: 'bg-sky-50', border: 'border-sky-200', icon: 'bg-sky-100 text-sky-600', badge: 'bg-sky-100 text-sky-700', accent: 'text-sky-600', ring: 'ring-sky-500' },
  { key: 'red', label: 'Rojo', swatch: 'bg-red-500', bg: 'bg-red-50', border: 'border-red-200', icon: 'bg-red-100 text-red-600', badge: 'bg-red-100 text-red-700', accent: 'text-red-600', ring: 'ring-red-500' },
  { key: 'yellow', label: 'Amarillo', swatch: 'bg-yellow-500', bg: 'bg-yellow-50', border: 'border-yellow-200', icon: 'bg-yellow-100 text-yellow-600', badge: 'bg-yellow-100 text-yellow-700', accent: 'text-yellow-600', ring: 'ring-yellow-500' },
  { key: 'slate', label: 'Pizarra', swatch: 'bg-slate-500', bg: 'bg-slate-50', border: 'border-slate-200', icon: 'bg-slate-100 text-slate-600', badge: 'bg-slate-100 text-slate-700', accent: 'text-slate-600', ring: 'ring-slate-500' },
];

const getColorConfig = (colorKey: string) => {
  return COLOR_OPTIONS.find(c => c.key === colorKey) || COLOR_OPTIONS[0];
};

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => {
  const value = `${String(hour).padStart(2, '0')}:00`;
  return { value, label: value };
});

const normalizeHourToFullHour = (value: string | null | undefined) => {
  const raw = (value || '').trim();
  if (!raw) return '00:00';
  const [hourPart] = raw.split(':');
  const hour = Number(hourPart);
  const safeHour = Number.isFinite(hour) ? Math.max(0, Math.min(23, hour)) : 0;
  return `${String(safeHour).padStart(2, '0')}:00`;
};

export default function PlanesPage() {
  const { sucursalActiva } = useSucursal();
  const [planes, setPlanes] = useState<Plan[]>([]);
  const [availableRecurringProviders, setAvailableRecurringProviders] = useState<RecurringProvider[]>([]);
  const [loadingRecurringProviders, setLoadingRecurringProviders] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActivo, setFilterActivo] = useState<string>('todos');

  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    precio: 0,
    duracion_dias: 30,
    modalidad_cobro: 'unico' as 'unico' | 'recurrente',
    proveedor_recurrente: null as RecurringProvider | null,
    proveedores_recurrentes: [] as RecurringProvider[],
    visible_kiosco: false,
    permitir_webpay_plus_kiosco: false,
    max_dias_semana: '' as '' | number,
    max_sesiones_periodo: '' as '' | number,
    permitir_multiples_reservas_dia: false,
    acceso_horario: 'todo_dia' as 'todo_dia' | 'manana' | 'tarde',
    acceso_hora_inicio: '10:00',
    acceso_hora_fin: '12:00',
    beneficios: [''],
    activo: true,
    visible_app_mobile: true,
    color: 'teal',
  });

  useEffect(() => {
    fetchPlanes();
    fetchRecurringProviderAvailability();
  }, [sucursalActiva?.id, sucursalActiva?.gimnasio_id]);

  const fetchRecurringProviderAvailability = async () => {
    const gimnasioId = sucursalActiva?.gimnasio_id;
    if (!gimnasioId || !sucursalActiva?.id) {
      setAvailableRecurringProviders([]);
      setLoadingRecurringProviders(false);
      return;
    }

    setLoadingRecurringProviders(true);
    try {
      const { data: branchRows, error: branchError } = await supabase
        .from('pago_configuracion')
        .select('online_activo,mercadopago_activo,flow_activo,fintoc_activo,transbank_oneclick_activo')
        .eq('gimnasio_id', gimnasioId)
        .eq('sucursal_id', sucursalActiva.id)
        .order('updated_at', { ascending: false })
        .limit(1);
      if (branchError) throw branchError;

      let config = branchRows?.[0];
      if (!config) {
        const { data: gymRows, error: gymError } = await supabase
          .from('pago_configuracion')
          .select('online_activo,mercadopago_activo,flow_activo,fintoc_activo,transbank_oneclick_activo')
          .eq('gimnasio_id', gimnasioId)
          .is('sucursal_id', null)
          .order('updated_at', { ascending: false })
          .limit(1);
        if (gymError) throw gymError;
        config = gymRows?.[0];
      }

      if (!config?.online_activo) {
        setAvailableRecurringProviders([]);
        return;
      }

      setAvailableRecurringProviders(RECURRING_PROVIDER_OPTIONS
        .filter((option) => ({
          mercadopago: config.mercadopago_activo === true,
          flow: config.flow_activo === true,
          fintoc: config.fintoc_activo === true,
          transbank_oneclick_mall: config.transbank_oneclick_activo === true,
        })[option.value])
        .map((option) => option.value));
    } catch (providerError) {
      console.error('Error loading recurring payment providers:', providerError);
      setAvailableRecurringProviders([]);
    } finally {
      setLoadingRecurringProviders(false);
    }
  };

  const getErrorMessage = (err: unknown, fallback: string) => {
    if (!err) return fallback;
    if (typeof err === 'string' && err.trim()) return err;

    // Supabase/PostgREST errors are often plain objects.
    if (typeof err === 'object') {
      const anyErr = err as any;
      const message = typeof anyErr.message === 'string' ? anyErr.message : '';
      const details = typeof anyErr.details === 'string' ? anyErr.details : '';
      const hint = typeof anyErr.hint === 'string' ? anyErr.hint : '';

      // Friendly hint for our new column if the DB wasn't migrated.
      if (
        (message + ' ' + details + ' ' + hint)
          .toLowerCase()
          .includes('max_dias_semana') &&
        (message + ' ' + details + ' ' + hint)
          .toLowerCase()
          .includes('does not exist')
      ) {
        return 'Tu base de datos aún no tiene el campo "max_dias_semana". Ejecuta la migración SQL (agrega la columna) y vuelve a intentar.';
      }

      if (
        (message + ' ' + details + ' ' + hint)
          .toLowerCase()
          .includes('acceso_horario') &&
        (message + ' ' + details + ' ' + hint)
          .toLowerCase()
          .includes('does not exist')
      ) {
        return 'Tu base de datos aún no tiene los campos de acceso por horario (acceso_horario/acceso_hora_inicio/acceso_hora_fin). Ejecuta la migración SQL de planes (acceso por horario) y vuelve a intentar.';
      }

      const combined = [message, details, hint].filter(Boolean).join(' - ');
      if (combined.trim()) return combined;
    }

    if (err instanceof Error && err.message.trim()) return err.message;
    return fallback;
  };

  const fetchPlanes = async () => {
    try {
      setLoading(true);
      setError(null);

      const gimnasioId = sucursalActiva?.gimnasio_id;
      if (!gimnasioId) {
        setPlanes([]);
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('planes')
        .select('*')
        .eq('gimnasio_id', gimnasioId)
        .order('precio', { ascending: true });

      if (fetchError) throw fetchError;

      setPlanes(data || []);
    } catch (err) {
      setError(getErrorMessage(err, 'Error al cargar planes'));
      console.error('Error fetching planes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setError(null);

      const accesoHoraInicioNormalizada = normalizeHourToFullHour(formData.acceso_hora_inicio);
      const accesoHoraFinNormalizada = normalizeHourToFullHour(formData.acceso_hora_fin);

      if (formData.acceso_horario !== 'todo_dia' && accesoHoraInicioNormalizada >= accesoHoraFinNormalizada) {
        setError('La hora de inicio debe ser menor que la hora de término.');
        return;
      }
      if (formData.modalidad_cobro === 'recurrente' && formData.proveedores_recurrentes.length === 0) {
        setError('Selecciona al menos un proveedor para los cobros recurrentes.');
        return;
      }
      if (formData.modalidad_cobro === 'recurrente' && formData.proveedores_recurrentes.some((provider) => !availableRecurringProviders.includes(provider))) {
        setError('Uno de los proveedores seleccionados ya no está habilitado en Métodos de Pago.');
        return;
      }

      const gimnasioId = sucursalActiva?.gimnasio_id || null;
      if (!gimnasioId) {
        setError('Debes seleccionar una sucursal activa para gestionar planes.');
        return;
      }

      const planData = {
        gimnasio_id: gimnasioId,
        nombre: formData.nombre,
        descripcion: formData.descripcion,
        precio: formData.precio,
        duracion_dias: formData.duracion_dias,
        modalidad_cobro: formData.modalidad_cobro,
        proveedor_recurrente: formData.modalidad_cobro === 'recurrente' ? formData.proveedores_recurrentes[0] : null,
        proveedores_recurrentes: formData.modalidad_cobro === 'recurrente' ? formData.proveedores_recurrentes : [],
        visible_kiosco: formData.visible_kiosco,
        permitir_webpay_plus_kiosco: formData.visible_kiosco && formData.permitir_webpay_plus_kiosco,
        max_dias_semana: formData.max_dias_semana === '' ? null : formData.max_dias_semana,
        max_sesiones_periodo: formData.max_sesiones_periodo === '' ? null : formData.max_sesiones_periodo,
        permitir_multiples_reservas_dia: !!formData.permitir_multiples_reservas_dia,
        acceso_horario: formData.acceso_horario,
        acceso_hora_inicio: formData.acceso_horario === 'todo_dia' ? null : accesoHoraInicioNormalizada,
        acceso_hora_fin: formData.acceso_horario === 'todo_dia' ? null : accesoHoraFinNormalizada,
        beneficios: formData.beneficios.filter(b => b.trim() !== ''),
        activo: formData.activo,
        visible_app_mobile: formData.visible_app_mobile,
        color: formData.color,
        updated_at: new Date().toISOString(),
      };

      if (editingPlan) {
        const { error: updateError } = await supabase
          .from('planes')
          .update(planData)
          .eq('id', editingPlan.id)
          .eq('gimnasio_id', gimnasioId);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('planes')
          .insert([planData]);

        if (insertError) throw insertError;
      }

      setShowModal(false);
      resetForm();
      fetchPlanes();
    } catch (err) {
      setError(getErrorMessage(err, 'Error al guardar plan'));
      console.error('Error saving plan:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este plan? Los usuarios con este plan no serán afectados.')) return;

    try {
      setError(null);
      const gimnasioId = sucursalActiva?.gimnasio_id;
      if (!gimnasioId) {
        setError('Debes seleccionar una sucursal activa para gestionar planes.');
        return;
      }
      const { error: deleteError } = await supabase
        .from('planes')
        .delete()
        .eq('id', id)
        .eq('gimnasio_id', gimnasioId);

      if (deleteError) throw deleteError;

      fetchPlanes();
    } catch (err) {
      setError(getErrorMessage(err, 'Error al eliminar plan'));
      console.error('Error deleting plan:', err);
    }
  };

  const handleToggleActivo = async (plan: Plan) => {
    try {
      setError(null);
      const gimnasioId = sucursalActiva?.gimnasio_id;
      if (!gimnasioId) {
        setError('Debes seleccionar una sucursal activa para gestionar planes.');
        return;
      }
      const { error: updateError } = await supabase
        .from('planes')
        .update({ activo: !plan.activo, updated_at: new Date().toISOString() })
        .eq('id', plan.id)
        .eq('gimnasio_id', gimnasioId);

      if (updateError) throw updateError;

      fetchPlanes();
    } catch (err) {
      setError(getErrorMessage(err, 'Error al actualizar plan'));
      console.error('Error updating plan:', err);
    }
  };

  const handleEdit = (plan: Plan) => {
    setEditingPlan(plan);
    const accesoHorario = (plan.acceso_horario || 'todo_dia') as 'todo_dia' | 'manana' | 'tarde';
    const configuredProviders = plan.proveedores_recurrentes?.length
      ? plan.proveedores_recurrentes
      : [plan.proveedor_recurrente].filter(Boolean) as RecurringProvider[];
    const activeConfiguredProviders = configuredProviders.filter((provider) => availableRecurringProviders.includes(provider));
    setFormData({
      nombre: plan.nombre,
      descripcion: plan.descripcion || '',
      precio: plan.precio,
      duracion_dias: plan.duracion_dias,
      modalidad_cobro: plan.modalidad_cobro || 'unico',
      proveedor_recurrente: plan.modalidad_cobro === 'recurrente'
        ? (activeConfiguredProviders[0] || null)
        : null,
      proveedores_recurrentes: plan.modalidad_cobro === 'recurrente'
        ? activeConfiguredProviders
        : [],
      visible_kiosco: plan.visible_kiosco === true,
      permitir_webpay_plus_kiosco: plan.permitir_webpay_plus_kiosco === true,
      max_dias_semana: plan.max_dias_semana ?? '',
      max_sesiones_periodo: plan.max_sesiones_periodo ?? '',
      permitir_multiples_reservas_dia: plan.permitir_multiples_reservas_dia === true,
      acceso_horario: accesoHorario,
      acceso_hora_inicio: normalizeHourToFullHour(plan.acceso_hora_inicio || '10:00'),
      acceso_hora_fin: normalizeHourToFullHour(plan.acceso_hora_fin || '12:00'),
      beneficios: plan.beneficios?.length > 0 ? plan.beneficios : [''],
      activo: plan.activo,
      visible_app_mobile: plan.visible_app_mobile !== false,
      color: plan.color || 'teal',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      descripcion: '',
      precio: 0,
      duracion_dias: 30,
      modalidad_cobro: 'unico',
      proveedor_recurrente: null,
      proveedores_recurrentes: [],
      visible_kiosco: false,
      permitir_webpay_plus_kiosco: false,
      max_dias_semana: '',
      max_sesiones_periodo: '',
      permitir_multiples_reservas_dia: false,
      acceso_horario: 'todo_dia',
      acceso_hora_inicio: '10:00',
      acceso_hora_fin: '12:00',
      beneficios: [''],
      activo: true,
      visible_app_mobile: true,
      color: 'teal',
    });
    setEditingPlan(null);
  };

  const formatAccessWindow = (plan: Plan) => {
    const mode = (plan.acceso_horario || 'todo_dia') as string;
    if (mode === 'todo_dia') return 'Todo el día';
    const start = (plan.acceso_hora_inicio || '').toString().slice(0, 5);
    const end = (plan.acceso_hora_fin || '').toString().slice(0, 5);
    if (start && end) return `${start} - ${end}`;
    return 'Sin rango';
  };

  const addBeneficio = () => {
    setFormData({ ...formData, beneficios: [...formData.beneficios, ''] });
  };

  const removeBeneficio = (index: number) => {
    const newBeneficios = formData.beneficios.filter((_, i) => i !== index);
    setFormData({ ...formData, beneficios: newBeneficios.length > 0 ? newBeneficios : [''] });
  };

  const updateBeneficio = (index: number, value: string) => {
    const newBeneficios = [...formData.beneficios];
    newBeneficios[index] = value;
    setFormData({ ...formData, beneficios: newBeneficios });
  };

  const filteredPlanes = planes.filter((plan) => {
    const matchesSearch = plan.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.descripcion?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesActivo = filterActivo === 'todos' ||
      (filterActivo === 'activo' && plan.activo) ||
      (filterActivo === 'inactivo' && !plan.activo);
    return matchesSearch && matchesActivo;
  });

  const estadisticas = {
    total: planes.length,
    activos: planes.filter(p => p.activo).length,
    inactivos: planes.filter(p => !p.activo).length,
    precioPromedio: planes.length > 0
      ? Math.round(planes.reduce((acc, p) => acc + p.precio, 0) / planes.length)
      : 0,
  };

  const getDuracionLabel = (dias: number) => {
    if (dias === 1) return '1 día';
    if (dias < 30) return `${dias} días`;
    if (dias === 30) return '1 mes';
    if (dias < 365) return `${Math.round(dias / 30)} meses`;
    if (dias === 365) return '1 año';
    return `${Math.round(dias / 365)} años`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-teal-500 animate-spin"></i>
            <p className="mt-4 text-gray-600">Cargando planes...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Planes de Membresía</h1>
            <p className="text-gray-600 mt-1">Gestiona los planes y precios del gimnasio</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-teal-500 text-white px-6 py-3 rounded-lg hover:bg-teal-600 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line text-lg"></i>
            Nuevo Plan
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {error}
          </div>
        )}

        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Planes</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{estadisticas.total}</p>
              </div>
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <i className="ri-price-tag-3-line text-2xl text-teal-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Planes Activos</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{estadisticas.activos}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Planes Inactivos</p>
                <p className="text-3xl font-bold text-gray-600 mt-1">{estadisticas.inactivos}</p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <i className="ri-close-circle-line text-2xl text-gray-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Precio Promedio</p>
                <p className="text-3xl font-bold text-teal-600 mt-1">
                  ${estadisticas.precioPromedio.toLocaleString('es-CL')}
                </p>
              </div>
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-2xl text-teal-600"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Buscar por nombre o descripción..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
            <select
              value={filterActivo}
              onChange={(e) => setFilterActivo(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="todos">Todos los estados</option>
              <option value="activo">Activos</option>
              <option value="inactivo">Inactivos</option>
            </select>
          </div>
        </div>

        {/* Grid de Planes */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredPlanes.map((plan) => {
            const colors = getColorConfig(plan.color || 'teal');
            return (
              <div
                key={plan.id}
                className={`${colors.bg} border-2 ${colors.border} rounded-xl p-6 relative transition-all hover:shadow-lg flex flex-col h-full ${
                  !plan.activo ? 'opacity-60' : ''
                }`}
              >
                {/* Badges de estado y visibilidad */}
                <span className={`absolute top-4 left-4 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                  plan.visible_app_mobile !== false
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {plan.visible_app_mobile !== false ? 'Visible en app' : 'Oculto en app'}
                </span>
                <div className="absolute top-4 right-4">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                    plan.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {plan.activo ? 'Activo' : 'Inactivo'}
                  </span>
                </div>

                {/* Icono y nombre */}
                  <div className="flex items-start gap-4 mb-4 mt-8">
                    <div className={`w-14 h-14 rounded-xl ${colors.icon} flex items-center justify-center flex-shrink-0`}>
                      <i className="ri-vip-crown-2-line text-2xl"></i>
                    </div>
                    <div className="flex-1 pt-1 min-w-0">
                      <h3 className="text-xl font-bold text-gray-900 line-clamp-2 pr-16">{plan.nombre}</h3>
                      <p className="text-sm text-gray-600 mt-1">{getDuracionLabel(plan.duracion_dias)}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Acceso: {plan.max_dias_semana && plan.max_dias_semana > 0 ? `${plan.max_dias_semana} días/semana` : 'Ilimitado'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Sesiones: {plan.max_sesiones_periodo && plan.max_sesiones_periodo > 0 ? `${plan.max_sesiones_periodo} por período` : 'Ilimitadas'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Reservas por día: {plan.permitir_multiples_reservas_dia ? 'Múltiples permitidas' : 'Máximo 1'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Horario: {formatAccessWindow(plan)}
                      </p>
                      <p className="text-xs font-medium text-gray-600 mt-1">
                        Cobro: {plan.modalidad_cobro === 'recurrente'
                          ? `Mensualidad automática · ${(plan.proveedores_recurrentes?.length ? plan.proveedores_recurrentes : [plan.proveedor_recurrente || 'mercadopago']).map((provider) => recurringProviderLabel(provider as RecurringProvider)).join(' / ')}`
                          : 'Pago único · Webpay Plus'}
                      </p>
                      {plan.visible_kiosco && plan.permitir_webpay_plus_kiosco && (
                        <p className="text-xs font-semibold text-emerald-700 mt-1">Tablet: Webpay Plus habilitado</p>
                      )}
                    </div>
                  </div>

                {/* Precio */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className={`text-4xl font-bold ${colors.accent}`}>
                      ${plan.precio.toLocaleString('es-CL')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">CLP / {getDuracionLabel(plan.duracion_dias)}</p>
                </div>

                {/* Descripción */}
                {plan.descripcion && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{plan.descripcion}</p>
                )}

                {/* Beneficios */}
                <div className="flex-1 mb-6">
                  {plan.beneficios && plan.beneficios.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Beneficios</p>
                      <ul className="space-y-2">
                        {plan.beneficios.slice(0, 4).map((beneficio, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <i className={`ri-check-line ${colors.accent} mt-0.5 flex-shrink-0`}></i>
                            <span>{beneficio}</span>
                          </li>
                        ))}
                        {plan.beneficios.length > 4 && (
                          <li className="text-sm text-gray-500 pl-5">
                            +{plan.beneficios.length - 4} más...
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Acciones */}
                <div className="flex items-center gap-2 pt-4 border-t border-gray-200 mt-auto">
                  <button
                    onClick={() => handleEdit(plan)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm whitespace-nowrap"
                  >
                    <i className="ri-edit-line"></i>
                    Editar
                  </button>
                  <button
                    onClick={() => handleToggleActivo(plan)}
                    className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                      plan.activo
                        ? 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                        : 'bg-green-100 text-green-600 hover:bg-green-200'
                    }`}
                    title={plan.activo ? 'Desactivar' : 'Activar'}
                  >
                    <i className={plan.activo ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                  <button
                    onClick={() => handleDelete(plan.id)}
                    className="w-10 h-10 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer"
                    title="Eliminar"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredPlanes.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-price-tag-3-line text-4xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No se encontraron planes</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm || filterActivo !== 'todos'
                ? 'Intenta ajustar los filtros de búsqueda'
                : 'Crea tu primer plan de membresía'}
            </p>
            {!searchTerm && filterActivo === 'todos' && (
              <button
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>
                Crear Plan
              </button>
            )}
          </div>
        )}

        {/* Modal de Crear/Editar Plan */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {editingPlan ? 'Editar Plan' : 'Nuevo Plan'}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {editingPlan ? 'Modifica los detalles del plan' : 'Crea un nuevo plan de membresía'}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                <div className="space-y-5">
                  {/* Nombre y Estado */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre del Plan *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.nombre}
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        placeholder="Ej: Plan Premium"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Estado
                      </label>
                      <select
                        value={formData.activo ? 'activo' : 'inactivo'}
                        onChange={(e) => setFormData({ ...formData, activo: e.target.value === 'activo' })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                      >
                        <option value="activo">Activo</option>
                        <option value="inactivo">Inactivo</option>
                      </select>
                    </div>
                  </div>

                  <label className="flex items-center justify-between gap-4 p-4 border border-gray-200 rounded-lg cursor-pointer">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Visible en la aplicación móvil</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Si lo desactivas, el plan seguirá operativo pero no podrá contratarse desde la app.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={formData.visible_app_mobile}
                      onChange={(e) => setFormData({ ...formData, visible_app_mobile: e.target.checked })}
                    />
                    <span className="relative flex-shrink-0 w-11 h-6 rounded-full bg-gray-300 transition-colors peer-checked:bg-teal-600 peer-focus-visible:ring-2 peer-focus-visible:ring-teal-500 peer-focus-visible:ring-offset-2 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:after:translate-x-5"></span>
                  </label>

                  {/* Color del Plan */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Color del Plan
                    </label>
                    <div className="grid grid-cols-6 gap-3">
                      {COLOR_OPTIONS.map((color) => (
                        <button
                          key={color.key}
                          type="button"
                          onClick={() => setFormData({ ...formData, color: color.key })}
                          className={`group flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 transition-all cursor-pointer ${
                            formData.color === color.key
                              ? `${color.border} ${color.bg} ring-2 ${color.ring} ring-offset-1`
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-full ${color.swatch} transition-transform ${
                            formData.color === color.key ? 'scale-110' : 'group-hover:scale-105'
                          }`}></div>
                          <span className={`text-xs font-medium ${
                            formData.color === color.key ? 'text-gray-900' : 'text-gray-500'
                          }`}>{color.label}</span>
                        </button>
                      ))}
                    </div>
                    {/* Preview */}
                    {(() => {
                      const previewColor = getColorConfig(formData.color);
                      return (
                        <div className={`mt-4 ${previewColor.bg} border ${previewColor.border} rounded-lg p-4 flex items-center gap-3`}>
                          <div className={`w-10 h-10 rounded-lg ${previewColor.icon} flex items-center justify-center flex-shrink-0`}>
                            <i className="ri-vip-crown-2-line text-lg"></i>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-800">
                              {formData.nombre || 'Nombre del plan'}
                            </p>
                            <p className={`text-xs ${previewColor.accent} font-medium`}>Vista previa del color seleccionado</p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Descripción */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descripción
                    </label>
                    <textarea
                      value={formData.descripcion}
                      onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                      placeholder="Describe brevemente el plan..."
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                    />
                  </div>

                  {/* Precio, Duración y límite semanal */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Precio (CLP) *
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="number"
                          required
                          min="0"
                          step="1"
                          value={formData.precio}
                          onChange={(e) => setFormData({ ...formData, precio: Number(e.target.value) })}
                          className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Duración (días) *
                      </label>
                      <select
                        value={formData.duracion_dias}
                        onChange={(e) => setFormData({ ...formData, duracion_dias: Number(e.target.value) })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                      >
                        <option value={1}>1 día</option>
                        <option value={7}>7 días (1 semana)</option>
                        <option value={15}>15 días</option>
                        <option value={30}>30 días (1 mes)</option>
                        <option value={60}>60 días (2 meses)</option>
                        <option value={90}>90 días (3 meses)</option>
                        <option value={180}>180 días (6 meses)</option>
                        <option value={365}>365 días (1 año)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Días por semana (opcional)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={formData.max_dias_semana}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setFormData({
                            ...formData,
                            max_dias_semana: raw === "" ? "" : Math.max(0, Number(raw)),
                          });
                        }}
                        placeholder="Ej: 2"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Limita días distintos de asistencia por semana. 0 o vacío = ilimitado.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sesiones por período (opcional)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={formData.max_sesiones_periodo}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setFormData({
                            ...formData,
                            max_sesiones_periodo: raw === "" ? "" : Math.max(0, Number(raw)),
                          });
                        }}
                        placeholder="Ej: 12"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Cantidad total de reservas por vigencia. 0 o vacío = ilimitado.
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Modalidad de cobro
                    </label>
                    <select
                      value={formData.modalidad_cobro}
                      onChange={(e) => {
                        const modalidad = e.target.value as 'unico' | 'recurrente';
                        setFormData({
                          ...formData,
                          modalidad_cobro: modalidad,
                          proveedor_recurrente: modalidad === 'recurrente'
                            ? (formData.proveedor_recurrente || availableRecurringProviders[0] || null)
                            : null,
                          proveedores_recurrentes: modalidad === 'recurrente'
                            ? (formData.proveedores_recurrentes.length
                              ? formData.proveedores_recurrentes.filter((provider) => availableRecurringProviders.includes(provider))
                              : availableRecurringProviders.slice(0, 1))
                            : [],
                          visible_kiosco: modalidad === 'recurrente' ? formData.visible_kiosco : false,
                        });
                      }}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="unico">Pago único — Webpay Plus</option>
                      <option value="recurrente">Mensualidad automática — proveedor recurrente</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Los planes recurrentes nunca ofrecen Webpay Plus. Los planes de pago único nunca ofrecen Mercado Pago recurrente ni Oneclick.
                    </p>
                  </div>

                  {formData.modalidad_cobro === 'recurrente' && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-800 mb-2">
                          Proveedores disponibles para la mensualidad automática *
                        </label>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {RECURRING_PROVIDER_OPTIONS.filter((option) => availableRecurringProviders.includes(option.value)).map((option) => {
                            const checked = formData.proveedores_recurrentes.includes(option.value);
                            return <label key={option.value} className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer ${checked ? 'border-teal-500 bg-white' : 'border-blue-100 bg-blue-50/40'}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(event) => {
                                  const next = event.target.checked
                                    ? [...formData.proveedores_recurrentes, option.value]
                                    : formData.proveedores_recurrentes.filter((provider) => provider !== option.value);
                                  setFormData({ ...formData, proveedores_recurrentes: next, proveedor_recurrente: next[0] || null });
                                }}
                                className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                              />
                              <span className="text-sm font-medium text-gray-800">{option.label}</span>
                            </label>;
                          })}
                        </div>
                        {loadingRecurringProviders ? (
                          <p className="text-xs text-blue-700 mt-2">Cargando proveedores habilitados…</p>
                        ) : availableRecurringProviders.length > 0 ? (
                          <p className="text-xs text-blue-700 mt-2">
                            Solo aparecen los cobros recurrentes activos en Métodos de Pago para esta sucursal.
                          </p>
                        ) : (
                          <p className="text-xs text-amber-700 mt-2">
                            No hay proveedores recurrentes activos. Habilita uno en Configuración → Métodos de Pago.
                          </p>
                        )}
                      </div>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.visible_kiosco}
                          onChange={(e) => setFormData({ ...formData, visible_kiosco: e.target.checked })}
                          className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-800">Disponible en tablet de autoinscripción</p>
                          <p className="text-xs text-gray-600 mt-1">La tablet mostrará solo planes recurrentes marcados aquí.</p>
                        </div>
                      </label>
                      <label className={`flex items-start gap-3 ${formData.visible_kiosco ? 'cursor-pointer' : 'opacity-50'}`}>
                        <input
                          type="checkbox"
                          disabled={!formData.visible_kiosco}
                          checked={formData.permitir_webpay_plus_kiosco}
                          onChange={(e) => setFormData({ ...formData, permitir_webpay_plus_kiosco: e.target.checked })}
                          className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-800">Aceptar Webpay Plus desde la tablet</p>
                          <p className="text-xs text-gray-600 mt-1">Cobra el período actual con Transbank. No registra una renovación automática.</p>
                        </div>
                      </label>
                    </div>
                  )}

                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.permitir_multiples_reservas_dia}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            permitir_multiples_reservas_dia: e.target.checked,
                          })
                        }
                        className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          Permitir múltiples reservas por día
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Si está activo, el usuario puede reservar varios bloques el mismo día (se descuentan sesiones por cada reserva).
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Acceso por horario */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <p className="text-sm font-semibold text-gray-800 mb-1">Acceso por horario</p>
                    <p className="text-xs text-gray-600 mb-4">
                      Define la franja diaria en que este plan permite reservar y, cuando Smart Occupancy está activo, ingresar mediante las cámaras. Los cambios se sincronizan automáticamente con el control de acceso.
                    </p>

                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { key: 'manana', label: 'Mañana' },
                        { key: 'tarde', label: 'Tarde' },
                        { key: 'todo_dia', label: 'Todo el día' },
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setFormData({ ...formData, acceso_horario: opt.key as any })}
                          className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            formData.acceso_horario === opt.key
                              ? 'bg-white border-teal-300 text-teal-700 ring-2 ring-teal-200'
                              : 'bg-white/60 border-gray-200 text-gray-700 hover:bg-white'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>

                    {formData.acceso_horario !== 'todo_dia' ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Hora inicio</label>
                          <select
                            value={formData.acceso_hora_inicio}
                            onChange={(e) => setFormData({ ...formData, acceso_hora_inicio: normalizeHourToFullHour(e.target.value) })}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                          >
                            {HOUR_OPTIONS.map((hour) => (
                              <option key={hour.value} value={hour.value}>
                                {hour.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Hora término</label>
                          <select
                            value={formData.acceso_hora_fin}
                            onChange={(e) => setFormData({ ...formData, acceso_hora_fin: normalizeHourToFullHour(e.target.value) })}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                          >
                            {HOUR_OPTIONS.map((hour) => (
                              <option key={hour.value} value={hour.value}>
                                {hour.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <p className="md:col-span-2 text-xs text-gray-500">
                          Solo se permiten horas exactas (HH:00). La hora de término no está incluida en el acceso.
                        </p>
                      </div>
                    ) : null}
                  </div>

                  {/* Beneficios */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Beneficios
                      </label>
                      <button
                        type="button"
                        onClick={addBeneficio}
                        className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1 cursor-pointer"
                      >
                        <i className="ri-add-line"></i>
                        Agregar
                      </button>
                    </div>
                    <div className="space-y-2">
                      {formData.beneficios.map((beneficio, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div className="flex-1 relative">
                            <i className="ri-check-line absolute left-3 top-1/2 -translate-y-1/2 text-green-500"></i>
                            <input
                              type="text"
                              value={beneficio}
                              onChange={(e) => updateBeneficio(index, e.target.value)}
                              placeholder="Ej: Acceso ilimitado al gimnasio"
                              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                            />
                          </div>
                          {formData.beneficios.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeBeneficio(index)}
                              className="w-10 h-10 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg cursor-pointer"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Botones */}
                <div className="flex gap-3 mt-8 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {editingPlan ? 'Guardar Cambios' : 'Crear Plan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
